<script lang="ts">
	// T29 Shift (UC-60): this till's cash shift, from Orders. One shift and
	// one line for the last closed one, nothing more. On a till that is its
	// own cash point it opens (T30) or closes (T31) the shift and prints the
	// last report again; on a till whose cash goes to another till it only
	// says where, and that till's shift as this one last confirmed it.
	// Under the open shift, Cash out / in (T35, UC-67) and its movements,
	// newest first, each with its note or its driver float's driver, with
	// their totals and never an expected figure: no till
	// screen shows the open shift's expected cash before the blind count.
	// Under the last closed line, Due at close split by driver when that close
	// holds a split (UC-60.R7), with the report's line.

	import { onDestroy, onMount } from 'svelte';
	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Loading from '../components/Loading.svelte';
	import Notice from '../components/Notice.svelte';
	import Row from '../components/Row.svelte';
	import StatCard from '../components/StatCard.svelte';
	import { formatMinor } from '../format/money';
	import { overShort } from '../format/shift';
	import { formatClock } from '../format/time';
	import { fill, message, type Language } from '../i18n/messages';
	import {
		forgetMovementDrawer,
		movementDrawer,
		openMovementDrawer,
	} from '../till/cashMovement.svelte';
	import {
		loadShift,
		payNotice,
		printShiftAgain,
		shiftBody,
		shiftState,
	} from '../till/shift.svelte';
	import DueByDriver from './DueByDriver.svelte';

	interface Props {
		language: Language;
		minorDigits: number;
		onOpenShift: () => void;
		onCloseShift: () => void;
		/** Cash out / in: the Cash movement sheet (T35) over this screen. */
		onCashMovement: () => void;
		onBack: () => void;
		/**
		 * Close shift (T31) is open over this screen: its count is typed blind,
		 * so the float, which the expected cash starts from, does not show
		 * beside the sheet.
		 */
		closing: boolean;
	}

	let { language, minorDigits, onOpenShift, onCloseShift, onCashMovement, onBack, closing }: Props =
		$props();

	let loading = $state(true);
	let slow = $state(false);
	let failed = $state(false);
	let printing = $state(false);
	let printSlow = $state(false);
	let printFailure = $state('');

	const body = $derived(shiftBody());
	const there = $derived(shiftState());
	const notice = $derived(payNotice(language, there));
	const movements = $derived([...(body?.open?.movements ?? [])].reverse());
	const drawer = $derived(movementDrawer());
	let drawerBusy = $state(false);

	async function openDrawer() {
		if (drawerBusy) return;
		drawerBusy = true;
		try {
			await openMovementDrawer();
		} finally {
			drawerBusy = false;
		}
	}

	async function load() {
		loading = true;
		failed = false;
		const timer = setTimeout(() => (slow = true), 300);
		try {
			await loadShift();
		} catch {
			failed = true;
		} finally {
			clearTimeout(timer);
			loading = false;
			slow = false;
		}
	}

	onMount(() => {
		void load();
	});

	// A movement's drawer line belongs to this visit of Shift: leaving it,
	// by Back or any other way, forgets it.
	onDestroy(forgetMovementDrawer);

	async function printAgain() {
		if (printing) return;
		printing = true;
		printFailure = '';
		const timer = setTimeout(() => (printSlow = true), 300);
		try {
			printFailure = (await printShiftAgain(language)) ?? '';
		} finally {
			clearTimeout(timer);
			printing = false;
			printSlow = false;
		}
	}
</script>

<div class="shift" data-testid="panel-shift" data-scroll="">
	<div class="bar">
		<Button
			testId="shift-back"
			icon="back"
			mirrorIcon={language === 'en'}
			label={message(language, 'ordersBack')}
			tone="secondary"
			size="sm"
			onPress={onBack}
		/>
		<h2>{message(language, 'shiftTitle')}</h2>
	</div>

	{#if body === null && loading}
		{#if slow}
			<div class="wait">
				<Loading
					testId="shift-loading"
					label={message(language, 'shiftLoading')}
					caption={message(language, 'shiftLoading')}
					size="md"
				/>
			</div>
		{/if}
	{:else if body === null || failed}
		<Band
			tone="flag"
			testId="shift-load-error"
			action={message(language, 'doneRetry')}
			actionTestId="shift-retry"
			actionBusy={loading}
			onAction={() => void load()}
		>
			{message(language, 'shiftLoadFailed')}
		</Band>
	{:else if body.role === 'pointing'}
		<p class="line" data-testid="shift-goes-to">
			{fill(message(language, 'shiftGoesTo'), body.cashPointName ?? '')}
		</p>
		{#if there?.open && there.openedAt}
			<p class="quiet" data-testid="shift-there-open">
				{fill(
					message(language, 'shiftThereOpen'),
					there.cashPointName ?? body.cashPointName ?? '',
					formatClock(there.openedAt, language),
				)}
			</p>
		{:else if notice}
			<Notice life="standing" tone="flag" testId="shift-there-notice">{notice.text}</Notice>
		{/if}
	{:else}
		<section class="current">
			{#if body.open}
				{#if !closing}
					<StatCard
						testId="shift-float"
						label={message(language, 'shiftFloat')}
						value={formatMinor(body.open.floatMinor, minorDigits)}
						mono
					/>
				{/if}
				<div class="who">
					<p class="line" data-testid="shift-open-since">
						{fill(message(language, 'shiftOpenSince'), formatClock(body.open.openedAt, language))}
					</p>
					<p class="quiet" data-testid="shift-opened-by">
						{fill(message(language, 'shiftOpenedBy'), body.open.openedByName)}
					</p>
				</div>
				<Button
					testId="shift-cash-movement"
					label={message(language, 'shiftCashOutIn')}
					tone="secondary"
					onPress={onCashMovement}
				/>
				<Button
					testId="shift-close"
					label={message(language, 'shiftClose')}
					tone="primary"
					onPress={onCloseShift}
				/>
			{:else}
				<p class="line" data-testid="shift-none">{message(language, 'shiftNoneOpen')}</p>
				<Button
					testId="shift-open"
					label={message(language, 'shiftOpen')}
					tone="primary"
					onPress={onOpenShift}
				/>
			{/if}
		</section>

		{#if body.open && !closing}
			<section class="movements" data-testid="shift-movements">
				<p class="line" dir="auto" data-testid="shift-cash-totals">
					{fill(
						message(language, 'shiftCashTotals'),
						formatMinor(body.open.cashOutMinor ?? 0, minorDigits),
						formatMinor(body.open.cashInMinor ?? 0, minorDigits),
					)}
				</p>
				{#if drawer.phase === 'failed' || drawer.phase === 'uncertain'}
					<Band
						tone="flag"
						testId="shift-drawer-error"
						action={message(language, 'printOpenDrawerNow')}
						actionTestId="shift-drawer-now"
						actionBusy={drawerBusy}
						onAction={() => void openDrawer()}
					>
						{message(language, drawer.phase === 'failed' ? 'doneDrawerFailed' : 'printHeldDrawer')}
					</Band>
				{/if}
				{#if movements.length > 0}
					<div class="list" style="--cols: auto minmax(0, 1fr) auto">
						{#each movements as move (move.id)}
							<Row testId="shift-movement-{move.id}">
								<span class="quiet" dir="ltr">{formatClock(move.at, language)}</span>
								<span class="what">
									<span class="label"
										>{message(
											language,
											move.direction === 'out' ? 'shiftCashOut' : 'shiftCashIn',
										)}</span
									>
									<span class="quiet" dir="auto"
										>{move.driverName
											? fill(
													message(language, 'shiftMovementDriverBy'),
													move.driverName,
													move.byName,
												)
											: move.note
												? fill(message(language, 'shiftMovementBy'), move.note, move.byName)
												: fill(message(language, 'shiftMovementByOnly'), move.byName)}</span
									>
								</span>
								<span class="figure" dir="ltr">{formatMinor(move.amountMinor, minorDigits)}</span>
							</Row>
						{/each}
					</div>
				{/if}
			</section>
		{/if}

		{#if body.lastClosed}
			<div class="last" style="--cols: minmax(0, 1fr) auto auto">
				<Row testId="shift-last">
					<span class="last-what">
						<span class="label">{message(language, 'shiftLastClosed')}</span>
						<span class="quiet">
							{fill(
								message(language, 'shiftClosedBy'),
								formatClock(body.lastClosed.closedAt, language),
								body.lastClosed.closedByName,
							)}
							{#if body.lastClosed.approvedByName}
								· {fill(message(language, 'shiftApprovedBy'), body.lastClosed.approvedByName)}
							{/if}
						</span>
					</span>
					<span class="figure" data-testid="shift-last-overshort">
						{overShort(language, body.lastClosed.overShortMinor, minorDigits)}
					</span>
					<Button
						testId="shift-print-again"
						icon="printer"
						label={message(language, 'shiftPrintAgain')}
						tone="secondary"
						size="sm"
						state={printSlow ? 'pending' : 'idle'}
						pendingLabel={message(language, 'shiftPrinting')}
						disabled={printing}
						onPress={() => void printAgain()}
					/>
				</Row>
			</div>
			<!-- Due at close by driver (UC-60.R7): a close an older store
			     wrote holds no split, and its line shows as it did. -->
			<DueByDriver
				{language}
				{minorDigits}
				closed={body.lastClosed}
				withTotal
				testId="shift-last-due"
			/>
			{#if printFailure}
				<Band tone="flag" testId="shift-print-error">{printFailure}</Band>
			{/if}
		{/if}
	{/if}
</div>

<style>
	.shift {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		padding: var(--space-7);
		overflow-y: auto;
		overflow-x: hidden;
	}

	.bar {
		flex: none;
		display: flex;
		align-items: center;
		gap: var(--space-5);
	}

	h2 {
		margin: 0;
		font-size: var(--text-h4);
		font-weight: 700;
	}

	.wait {
		flex: 1;
		display: grid;
		place-items: center;
	}

	.current {
		display: flex;
		align-items: center;
		gap: var(--space-7);
		flex-wrap: wrap;
	}

	.who {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.line {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 600;
		color: var(--text-strong);
	}

	.quiet {
		margin: 0;
		font-size: var(--text-base);
		color: var(--text-muted);
	}

	.movements {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.what {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		min-inline-size: 0;
	}

	.last-what {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.label {
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.figure {
		font-family: var(--font-mono);
		font-weight: 700;
	}
</style>
